import { DOCUMENT } from '@angular/common';
import {
  Component, Input, ChangeDetectionStrategy, ElementRef, Inject, Optional, OnInit, Attribute
} from '@angular/core';
import irisDesignTokens from '@sondermind/iris-design-tokens/design-tokens.json';

import { IrisIconRegistry } from './icon-registry.service';
import { IconUsageTypes } from './enums/icon-usage-types.enum';
import { IconColors } from './enums/icon-colors.enum';
import { IconSizes } from './enums/icon-sizes.enum';

@Component({
  selector: 'iris-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'iris-icon',
    /* eslint-disable @typescript-eslint/naming-convention */
    '[class.iris-icon--brand-1]': 'color === colorOptions.BRAND1',
    '[class.iris-icon--brand-2]': 'color === colorOptions.BRAND2',
    '[class.iris-icon--white]': 'color === colorOptions.WHITE',
    '[class.iris-icon--success]': 'color === colorOptions.SUCCESS',
    '[class.iris-icon--warning]': 'color === colorOptions.WARNING',
    '[class.iris-icon--danger]': 'color === colorOptions.DANGER',
    '[class.iris-icon--token-color]': 'colorToken !== ""',
    '[class.iris-icon--small]': 'size === sizeOptions.SM',
    '[class.iris-icon--medium]': 'size === sizeOptions.MD',
    '[class.iris-icon--large]': 'size === sizeOptions.LG',
    '[attr.aria-label]': 'ariaLabel'
    /* eslint-enable @typescript-eslint/naming-convention */
  }
})
export class IrisIconComponent implements OnInit {
  private svgIcon!: SVGElement;
  private colorOptions = IconColors;
  private sizeOptions = IconSizes;
  private usageTypes = IconUsageTypes;

  @Input() color: IconColors = this.colorOptions.BRAND1;
  @Input() size: IconSizes = this.sizeOptions.MD;
  @Input() ariaLabel!: string;
  @Input() usage: IconUsageTypes = this.usageTypes.DECORATIVE;
  @Input() colorToken: string = '';

  @Input() set name(iconName: string) {
    if (this.svgIcon) {
      this.element.nativeElement.removeChild(this.svgIcon);
    }

    // Gather Icon data from registry
    const svgData = this.irisIconRegistry.getIcon(iconName);

    // Create SVG Element
    this.svgIcon = this.svgElementFromString(svgData || '');

    // Append SVG Element to the DOM
    this.element.nativeElement.appendChild(this.svgIcon);
  }

  constructor(
    private element: ElementRef<HTMLElement>,
    private irisIconRegistry: IrisIconRegistry,
    @Attribute('aria-hidden') ariaHidden: string,
    @Optional() @Inject(DOCUMENT) private document: Document
  ) {
    // If the user has not explicitly set aria-hidden, mark the icon as hidden.
    if (!ariaHidden) {
      element.nativeElement.setAttribute('aria-hidden', 'true');
    }
  }

  ngOnInit(): void {
    // Handle usage type
    if (this.usage === IconUsageTypes.STAND_ALONE) {
      this.element.nativeElement.setAttribute('aria-hidden', 'false');
      this.element.nativeElement.setAttribute('role', 'img');
    }

    this.setColorFromToken();
  }

  private setColorFromToken(): void {
    if (this.colorToken.startsWith('$')) {
      const jsonKey = this.parseJsonKey(this.colorToken);
      const tokenColor = (irisDesignTokens as { [key: string]: string; })[jsonKey];
      if (tokenColor) {
        this.element.nativeElement.style.setProperty('--icon-token-color', tokenColor);
      } else {
        // eslint-disable-next-line no-console
        console.error(`iris-icon error: ${this.colorToken} is not a recognized Iris design token.`);
      }
    }
  }

  private svgElementFromString(svgContent: string): SVGElement {
    const div = this.document.createElement('DIV');
    div.innerHTML = svgContent;

    // Return svg element if available, if not, create one.
    return div.querySelector('svg') || this.document.createElementNS('http://www.w3.org/2000/svg', 'path');
  }

  /**
   * Translates the design token SCSS name to a key that can be used
   * to retrieve the token value from the token JSON file.
   * Turns '$color-dec-turquoise-03` into `colorDecTurquoise03`
   * @param sassToken The name of the Iris design token as it would usually be used in SCSS
   */
  private parseJsonKey(sassToken: string): string {
    const noDollarSign = sassToken.slice(1);
    const wordArray = noDollarSign.split('-');

    return this.buildCamelCaseFromArray(wordArray);
  }

  private buildCamelCaseFromArray(array: string[]): string {
    return array.reduce((prev, curr, index) => {
      const accumulator = prev + (index === 0
        ? curr.toLocaleLowerCase()
        : curr.charAt(0).toUpperCase() + curr.slice(1));
      return accumulator;
    }, '');
  }
}
